import { useState, useEffect, useRef } from 'react'
import { X, Loader, Check, AlertCircle, Eye, ShieldCheck, ShieldOff, Shield } from 'lucide-react'
import useAuthStore from '../store/auth-store'

// ─── 6-box OTP input ──────────────────────────────────────────────────────────
function InlineOtpInput({ value, onChange, disabled }) {
  const refs = Array.from({ length: 6 }, () => useRef(null))
  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const d = value.split(''); d[idx] = ''
      onChange(d.join(''))
      if (idx > 0) refs[idx - 1].current?.focus()
    }
  }
  const handleChange = (e, idx) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1)
    const d = value.padEnd(6, ' ').split(''); d[idx] = ch
    onChange(d.join('').replace(/ /g, ''))
    if (ch && idx < 5) refs[idx + 1].current?.focus()
  }
  const handlePaste = (e) => {
    e.preventDefault()
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    onChange(p)
    refs[Math.min(p.length, 5)].current?.focus()
  }
  return (
    <div className="flex items-center gap-1.5">
      {[0,1,2].map(i => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          disabled={disabled} value={value[i]||''}
          onChange={e=>handleChange(e,i)} onKeyDown={e=>handleKey(e,i)} onPaste={handlePaste}
          className="w-9 h-10 text-center text-base font-bold border-2 border-gray-300 rounded
                     focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition" />
      ))}
      <span className="text-gray-400 font-light">-</span>
      {[3,4,5].map(i => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          disabled={disabled} value={value[i]||''}
          onChange={e=>handleChange(e,i)} onKeyDown={e=>handleKey(e,i)} onPaste={handlePaste}
          className="w-9 h-10 text-center text-base font-bold border-2 border-gray-300 rounded
                     focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition" />
      ))}
    </div>
  )
}

export function TwoFactorModal({ isOpen, onClose }) {
  const { user, twoFactorApi } = useAuthStore()

  const [tfa, setTfa]                   = useState({ is_enabled: false, backup_codes_count: 0 })
  const [tfaLoading, setTfaLoading]     = useState(false)
  const [tfaPanel, setTfaPanel]         = useState(null) // null | 'setup' | 'disable' | 'backup'
  const [tfaQr, setTfaQr]               = useState('')
  const [tfaBackup, setTfaBackup]       = useState([])
  const [tfaOtp, setTfaOtp]             = useState('')
  const [tfaMsg, setTfaMsg]             = useState(null)

  const fetchTfaStatus = async () => {
    try {
      const res = await twoFactorApi.getStatus()
      setTfa(res.data)
    } catch {}
  }

  // Re-fetch status every time the modal opens; reset transient state on close
  useEffect(() => {
    if (isOpen && user) {
      fetchTfaStatus()
    }
    if (!isOpen) {
      setTfaPanel(null)
      setTfaOtp('')
      setTfaQr('')
      setTfaBackup([])
      setTfaMsg(null)
    }
  }, [isOpen, user])

  const handleTfaSetup = async () => {
    setTfaLoading(true); setTfaMsg(null)
    try {
      const res = await twoFactorApi.setup()
      setTfaQr(res.data.qr_code)
      setTfaPanel('setup')
      setTfaOtp('')
    } catch { setTfaMsg({ type: 'error', text: 'Failed to start setup. Please try again.' }) }
    finally { setTfaLoading(false) }
  }

  const handleTfaEnable = async () => {
    if (tfaOtp.length < 6) return
    setTfaLoading(true); setTfaMsg(null)
    try {
      await twoFactorApi.enable(tfaOtp)
      setTfaMsg({ type: 'success', text: '2FA enabled successfully! Your account is now protected.' })
      setTfaPanel(null); setTfaOtp(''); setTfaQr('')
      await fetchTfaStatus()
    } catch (err) {
      setTfaMsg({ type: 'error', text: err.response?.data?.error || 'Invalid code. Please try again.' })
      setTfaOtp('')
    } finally { setTfaLoading(false) }
  }

  const handleTfaDisable = async () => {
    if (tfaOtp.length < 6) return
    setTfaLoading(true); setTfaMsg(null)
    try {
      await twoFactorApi.disable(tfaOtp)
      setTfaMsg({ type: 'success', text: '2FA has been disabled.' })
      setTfaPanel(null); setTfaOtp('')
      await fetchTfaStatus()
    } catch (err) {
      setTfaMsg({ type: 'error', text: err.response?.data?.error || 'Invalid code. Please try again.' })
      setTfaOtp('')
    } finally { setTfaLoading(false) }
  }

  const handleViewBackupCodes = async () => {
    setTfaLoading(true)
    try {
      const res = await twoFactorApi.getBackupCodes()
      setTfaBackup(res.data.backup_codes || [])
      setTfaPanel('backup')
    } catch { setTfaMsg({ type: 'error', text: 'Failed to load backup codes.' }) }
    finally { setTfaLoading(false) }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100 sticky top-0 bg-white">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">Two-Factor Authentication</h2>
              <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full
                ${tfa.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {tfa.is_enabled ? <><ShieldCheck className="w-3.5 h-3.5" />Enabled</> : <><Shield className="w-3.5 h-3.5" />Disabled</>}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {tfa.is_enabled
                ? 'Your account is protected with Google Authenticator.'
                : 'Add an extra layer of security using Google Authenticator.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition ml-4"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {tfaMsg && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm
              ${tfaMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {tfaMsg.type === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
              {tfaMsg.text}
            </div>
          )}

          {/* Action buttons */}
          {tfaPanel === null && (
            <div className="flex flex-wrap gap-2">
              {!tfa.is_enabled ? (
                <button onClick={handleTfaSetup} disabled={tfaLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 transition"
                  style={{ backgroundColor: '#358ECD' }}>
                  {tfaLoading ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Enable Google Authenticator
                </button>
              ) : (
                <>
                  <button onClick={() => { setTfaPanel('disable'); setTfaOtp(''); setTfaMsg(null) }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition">
                    <ShieldOff className="w-4 h-4" /> Disable 2FA
                  </button>
                  <button onClick={handleViewBackupCodes} disabled={tfaLoading}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                    {tfaLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    View Backup Codes ({tfa.backup_codes_count} left)
                  </button>
                </>
              )}
            </div>
          )}

          {/* Setup panel */}
          {tfaPanel === 'setup' && (
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 space-y-5">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">1. Scan this QR code in Google Authenticator</p>
                <div className="flex justify-center">
                  <img src={tfaQr} alt="2FA QR" className="w-44 h-44 border border-gray-200 rounded-lg p-1 bg-white" />
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">2. Enter the 6-digit code to confirm</p>
                <InlineOtpInput value={tfaOtp} onChange={setTfaOtp} disabled={tfaLoading} />
              </div>

              <div className="flex gap-2">
                <button onClick={handleTfaEnable} disabled={tfaLoading || tfaOtp.length < 6}
                  className="px-5 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-50 transition"
                  style={{ backgroundColor: '#358ECD' }}>
                  {tfaLoading ? <Loader className="w-4 h-4 animate-spin inline" /> : 'Confirm & Enable'}
                </button>
                <button onClick={() => { setTfaPanel(null); setTfaOtp(''); setTfaMsg(null); setTfaQr('') }}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition">Cancel</button>
              </div>
            </div>
          )}

          {/* Disable panel */}
          {tfaPanel === 'disable' && (
            <div className="border border-red-200 rounded-xl p-5 bg-red-50 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Enter your Google Authenticator code to confirm</p>
              <InlineOtpInput value={tfaOtp} onChange={setTfaOtp} disabled={tfaLoading} />
              <div className="flex gap-2">
                <button onClick={handleTfaDisable} disabled={tfaLoading || tfaOtp.length < 6}
                  className="px-5 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition">
                  {tfaLoading ? <Loader className="w-4 h-4 animate-spin inline" /> : 'Disable 2FA'}
                </button>
                <button onClick={() => { setTfaPanel(null); setTfaOtp(''); setTfaMsg(null) }}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition">Cancel</button>
              </div>
            </div>
          )}

          {/* Backup codes panel */}
          {tfaPanel === 'backup' && (
            <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700 mb-3">Remaining Backup Codes ({tfaBackup.length})</p>
              {tfaBackup.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No backup codes remaining. Regenerate 2FA from setup.</p>
              ) : (
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  {tfaBackup.map((c, i) => (
                    <code key={i} className="text-xs bg-white border border-gray-200 font-mono px-3 py-1.5 rounded text-center text-gray-700">{c}</code>
                  ))}
                </div>
              )}
              <button onClick={() => { setTfaPanel(null); setTfaMsg(null) }}
                className="text-sm text-gray-500 hover:text-gray-700 transition">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
